import * as React from 'react';
import { observer } from 'mobx-react';
import { AssignUserViewStore } from 'nomst/Stores/Configuration/assignUserView.store';
import { HrRolesRow } from 'nomst/Components/WorkflowSettings/hrRolesRow';
import { AssignedUserLevel } from 'typings/enums';
import { RemoveUserDialog } from 'components/Common/removeUserDialog';
import { workflowSettingsViewStore } from 'utils/MobxDataManager/singletons';
import { Alert } from '@payscale/design';
import './workflowHrRoles.scss';

export interface IWorkflowHrRolesProps {
    readonly?: boolean;
}

interface IWorkflowHrRolesState {
    hrAssistants: AssignUserViewStore[];
}

export class WorkflowHrRoles extends React.Component<IWorkflowHrRolesProps, IWorkflowHrRolesState> {
    constructor(props: IWorkflowHrRolesProps) {
        super(props);

        this.state = { hrAssistants: workflowSettingsViewStore.assignUserHrAssistants };
        this.addHrAssistantRow = this.addHrAssistantRow.bind(this);
        this.renderHrAssistantRow = this.renderHrAssistantRow.bind(this);
        this.isUserHrApprover = this.isUserHrApprover.bind(this);
        this.isUserHrAssistant = this.isUserHrAssistant.bind(this);
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-workflow-hr-roles">
                {this.renderHrApproverSection()}
                {this.renderApproverWarning()}
                {this.renderHrAssistantsSection()}
                {this.renderRemoveAssignedUserWarningBox()}
            </div>
        );
    }

    private renderHrApproverSection(): React.ReactNode {
        return (
            <section className="nomst-workflow-hr-roles__administrator">
                <div className="nomst-workflow-hr-roles__role">HR Administrator</div>
                <p className="nomst-workflow-hr-roles__description">
                    Views full org, sends and approves teams and can edit configurations.
                    Only one user can be assigned to this role.
                </p>
                <table className="nomst-workflow-hr-roles__table">
                    <thead>
                        <tr>
                            <th>Access Group</th>
                            <th>Assigned User</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <HrRolesRow
                            assignUserViewStore={workflowSettingsViewStore.assignUserHrApprover}
                            assignedUserLevel={AssignedUserLevel.HrApprover}
                            readonly={this.props.readonly}
                            isUserAlreadyAssigned={this.isUserHrAssistant}
                        />
                    </tbody>
                </table>
            </section>
        );
    }

    private renderApproverWarning(): React.ReactNode {
        if (workflowSettingsViewStore.assignUserHrApprover.selectedUserId) {
            return null;
        }

        return (
            <Alert
                message="HR Administrator must be assigned to open the cycle"
                level="info"
            />
        );
    }

    private renderHrAssistantsSection(): React.ReactNode {
        return (
            <section className="nomst-workflow-hr-roles__reviewer">
                <div className="nomst-workflow-hr-roles__role">HR Reviewer</div>
                <p className="nomst-workflow-hr-roles__description">
                    Views full org and can edit configurations.
                </p>
                <table className="nomst-workflow-hr-roles__table">
                    <thead>
                        <tr>
                            <th>Access Group</th>
                            <th>Assigned User</th>
                            <th>
                                <button
                                    hidden={this.props.readonly}
                                    onClick={this.addHrAssistantRow}
                                    className="nomst-workflow-hr-roles__assign-user pxl-link"
                                >
                                    + Assign User
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.hrAssistants.map(this.renderHrAssistantRow)}
                    </tbody>
                </table>
            </section>
        );
    }

    private renderHrAssistantRow(hrAssistant: AssignUserViewStore, index: number): React.ReactNode {
        return (
            <HrRolesRow
                key={index}
                assignUserViewStore={hrAssistant}
                assignedUserLevel={AssignedUserLevel.HrAssistant}
                readonly={this.props.readonly}
                isUserAlreadyAssigned={this.isUserHrApprover}
            />
        );
    }

    private renderRemoveAssignedUserWarningBox(): React.ReactNode {
        const removeAssignedUserModalViewStore = workflowSettingsViewStore.removeAssignedUserStore;
        if (!removeAssignedUserModalViewStore?._assignedUserId) {
            return null;
        }

        return (
            <RemoveUserDialog />
        );
    }

    private addHrAssistantRow(): void {
        const newStore = workflowSettingsViewStore.getAssignUserViewStore();
        const newRow = this.state.hrAssistants;
        newRow.push(newStore);
        this.setState({ hrAssistants: newRow });
        workflowSettingsViewStore.addHrAssistantRow(newStore);
    }

    private isUserHrApprover(email: string): boolean {
        return workflowSettingsViewStore.isUserHrApprover(email);
    }

    private isUserHrAssistant(email: string): boolean {
        return workflowSettingsViewStore.isUserHrAssistant(email);
    }
}

export default observer(WorkflowHrRoles);